import { Injectable } from '@angular/core';
import { User } from '@mitel/cloudlink-sdk';
import { Subject, Subscription } from 'rxjs';
import { AppInjector } from './../../app-injector';
import { AdminService } from './admin.service';
import { OrdersService } from './orders.service';
import { SpinnerService } from './spinner.service';
import { MiVBUserService, FormattedMiVBUserTelephonyService } from './mivb-user.service';
import { AuthService } from './auth.service';
import { UserConfigurationComponent, MivbLicenseConfigurationComponent, asyncJobProcessingStatus, saveTypes } from '@mitel-internal/mivb-components';
import { HighLevelProductDetail, ProductLicense, ProductLicenseItemServiceInf } from './../interfaces/product-licenses-interfaces';
import {
  mitelOneLicenses, mivoiceBusinessLicenses, ProductLicenseBundleName, licenseUCCV40Tier, asyncJobStatus, asyncJobOwner, TemplateBadges,
  miVBLicenseType, licenseUCCEdition2Tier
} from './../constants/constants';
import { Util } from './../utils/Util';
import { FeatureToggleService } from './feature-toggle.service';
import { MiVBAccountSettingsService } from './mivb-account-settings.service';
import { MiVBProvisioningService } from './mivb-provisioning.service';
import { TranslateService } from '@ngx-translate/core';
import { UserMiVBSharedService } from './user-mivb-shared.service';
import { IntegCustomerAdminPortalService } from './integ-customer-admin-portal.service';
import { environment } from '../../../environments/environment';
import { MiVoiceBusinessConfigurationComponent } from '../../modules/users-single-edit/components/product-configs/mivoice-business-config/mivoice-business-config.component';
import { CustomValidators } from '../utils/CustomValidators';
import { MiVBAsyncJobData } from '../interfaces/async-job-data';
import { AsyncJobsService } from './async-jobs.service';
import { MiVBEmailService } from './mivb-email.service';
import { MiVBEmailIconData, UserMiVBDeploymentEmailHistoryTagsBody, UserMiVBEmailHistoryData, UserTagsDataAfterEmailEvent } from '../interfaces/mivb-emails-interfaces';
import { UserMiVBEmailEvents } from '../constants/mivb-general';
import * as _ from 'lodash';
import { EmployeeService } from '@mitel-internal/miccb-components';
import { UserProdMiCCBService } from './user-prod-miccb.service';
import { UserProdZappaPhoneService } from './user-prod-zappa-phone.service';
import { RBACAuthGuardService } from './../guards/rbac-auth-guard.service';
import { AssociationsService } from './associations.service';
import { Location } from './../interfaces/collections';

@Injectable({
  providedIn: 'root'
})
export class UserProdMiVBService implements ProductLicenseItemServiceInf {
  private adminService: AdminService;
  private spinnerService: SpinnerService;
  private ordersService: OrdersService;
  private miVBUserSvc: MiVBUserService;
  private authSvc: AuthService;
  private featureToggleService: FeatureToggleService;
  private mivbAccountSettingsService: MiVBAccountSettingsService;
  private mivbProvisioningService: MiVBProvisioningService;
  private translateService: TranslateService;
  private userMivbSharedService: UserMiVBSharedService;
  private capIntegService: IntegCustomerAdminPortalService;
  private asyncJobsService: AsyncJobsService;
  private mivbEmailService: MiVBEmailService;
  private employeeService: EmployeeService;
  private miccbService: UserProdMiCCBService;
  private zappaService: UserProdZappaPhoneService;
  private rbacAuthGuardService: RBACAuthGuardService;
  private associationsService: AssociationsService;

  subscription: Subscription;
  userTagsChangedSubscription: Subscription;

  public updateProdNLcnsEvent = new Subject<any>();
  public updateSubComponentToC = new Subject<any>();
  public updateSubComponentFormStatus = new Subject<any>();
  public heightToExtendParentContainer = new Subject<number>();
  public updateEmailIcon = new Subject<any>();

  cachedMivbUsersList: any[] = [];
  cachedConfiguration: {userId: string, mivbConfiguration: any};
  cachedTableOfContentsList: any[];
  capIsEnabled: boolean = false; // an attempt to not test the policy numerous times
  configurationDialogName: string;

  emailIcons: MiVBEmailIconData[] = [
    {
      src: '/assets/images/Spinner.png',
      tooltipKey: 'users_edit_page.email_in_progress_tooltip',
      tooltipData: {timestamp: undefined},
      imgClass: 'email-spinner-animate'
    },
    {
      src: '/assets/images/caution-tri-line-blue.svg',
      tooltipKey: 'users_edit_page.email_interrupted_tooltip',
      tooltipData: {timestamp: undefined}
    },
    {
      src: '/assets/images/email_success.svg',
      tooltipKey: 'users_edit_page.email_success_tooltip',
      tooltipData: {timestamp: undefined}
    },
    {
      src: '/assets/images/email_failed.svg',
      tooltipKey: 'users_edit_page.email_failed_tooltip',
      tooltipData: {timestamp: undefined}
    }
  ];

  userMiVBEmailHistoryData: UserMiVBEmailHistoryData = {
    userId: undefined,
    accountId: undefined,
    shouldEmailIconsShow: false,
    deploymentEmailHistory: undefined
  };

  emailInProgressMaxMinutes = 10; //max reasonable amount of time to wait for email operation to complete
  emailInProgressTimeoutId;

	private capPromise: Promise<boolean> = undefined;
  private currentAccountId: string = undefined;
  currentUserId: string = undefined;

  constructor() { }

  async initializeServices( user: User ) {
    if (!this.adminService)
      this.adminService = AppInjector.injector.get(AdminService);

    if (!this.miVBUserSvc)
      this.miVBUserSvc = AppInjector.injector.get(MiVBUserService);

    if (!this.mivbEmailService)
      this.mivbEmailService = AppInjector.injector.get(MiVBEmailService);

    this.resetServiceVariablesIfRequired(user);
    this.currentUserId = user.userId;

    if(!this.userTagsChangedSubscription){
      this.userTagsChangedSubscription = this.mivbEmailService.userTagsChanged.subscribe(async(data: UserTagsDataAfterEmailEvent) =>{
        if(data && data.userId && data.tags && data.accountId){
          if(this.currentUserId && (this.currentUserId === data.userId) && (user.accountId === data.accountId)){
            try {
              await this.setUserMiVBEmailHistoryData(user, data.tags);
              this.determineEmailIcon();
            } catch (error) {
              this.determineEmailIcon();
            }
          }
        }
      });
    }

    if (!this.ordersService)
    this.ordersService = AppInjector.injector.get(OrdersService);

    if (!this.spinnerService)
      this.spinnerService = AppInjector.injector.get(SpinnerService);

    if (!this.subscription)
      this.subscription = new Subscription();

    if (!this.authSvc)
      this.authSvc = AppInjector.injector.get(AuthService);

    if (!this.featureToggleService)
      this.featureToggleService = AppInjector.injector.get(FeatureToggleService);

    if (!this.mivbAccountSettingsService)
      this.mivbAccountSettingsService = AppInjector.injector.get(MiVBAccountSettingsService);

    if (!this.mivbProvisioningService)
      this.mivbProvisioningService = AppInjector.injector.get(MiVBProvisioningService);

    if (!this.translateService)
        this.translateService = AppInjector.injector.get(TranslateService);

    if (!this.userMivbSharedService)
        this.userMivbSharedService = AppInjector.injector.get(UserMiVBSharedService);

    if (!this.asyncJobsService)
        this.asyncJobsService = AppInjector.injector.get(AsyncJobsService);

    if (!this.capIntegService)
        this.capIntegService = AppInjector.injector.get(IntegCustomerAdminPortalService);

    if (!this.employeeService) {
        this.employeeService = AppInjector.injector.get(EmployeeService);
    }

    if (!this.miccbService) {
      this.miccbService = AppInjector.injector.get(UserProdMiCCBService);
    }

    if (!this.zappaService) {
      this.zappaService = AppInjector.injector.get(UserProdZappaPhoneService);
    }

    if (!this.rbacAuthGuardService) {
      this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);
    }

    this.associationsService ??= AppInjector.injector.get(AssociationsService);

    if ( user && this.currentAccountId != user.accountId ) // this policy is tested for almost every method, so lets pre-test it here
    {
      this.currentAccountId = user.accountId;
      this.capPromise = this.capIntegService.isCAPFeatureEnabled({accountId: user.accountId} as any);
    }

    if ( this.capPromise != undefined ) // done this way to prevent multiple requests and not waiting for the result when the accounts Id changed
    {
      return this.capPromise.then( enabled => {
        this.capIsEnabled = enabled;
        this.capPromise = undefined;
        this.evaluateEmailIcon(user);
        return;
      })
    }
    else
      return Promise.resolve();
  }

  async canBeAdded(user: User, product: ProductLicense, isSubComponent?: boolean): Promise<boolean> {
    await this.initializeServices(user);
    // there is a method in the shared service that does something similar... maybe use that if
    // the individual states are not needed - now it appears individual states are required
    // we need to determine if the CAP policy is OFF and there are MiVB licenses
    return this.ordersService.doesCustomerAccountHaveExistingProductSubsAndTier(
        environment.subscriptionIds.MiVoiceBusiness, ["MiVB_Premier",	"MiVB_Elite", "MiVB_Entry"]).then(existingSubs => {
          // actual licenses can only be assigned if there is a subscription
          if ( this.capIsEnabled == false && existingSubs == true)
          {
            // if we are here, then yes we are in pure mivb license scenario
            // check to see if there are any licenses remaining
            return this.getRemainingLicenses(user).then( count => {
              if ( count <= 0 )
              {
                product.statusString = 'users_edit_page.licensesNotAvailable';
                return false;
              }
              // there are licenses but is the user eligible
              if ( user.userVerified == true )
                return true;

              if (user.email && user.email.length > 0)
              {
                return this.adminService.userHasADuplicateEmail(user.accountId, user).then(hasDup => {
                    const validEmail = CustomValidators.userHasValidEmail(user);
                    return !hasDup && validEmail;
                 });

              }
              else
              {
                  // if user is verified but doesn't have an email then we allow it to get a license
                // User from MiCloud Connect are verified but they may have invalid email or no email. Email is not validated.
                  product.statusString = 'users_edit_page.user_not_eligible';
                  return false;
              }
            });
          }
          else if ( this.capIsEnabled == true )
            return true; // this is the pre-existing state but must require CAP to be on now

          // OK CAP is off and there are no subs so can't assign licenses to users
          return false;
    });
  }

  async canBeTemplated(user: User): Promise<boolean> {
    await this.initializeServices(user);
    return Promise.resolve(this.capIsEnabled);
  }

  async canBeSelected(user: User): Promise<boolean> {
    await this.initializeServices(user);
    return Promise.resolve(true);
  }

  async hasRequiredAccountIntegrations( user: User, accountIntegrations: string[] ):  Promise<boolean>
  {
    // for some reason this product is never refreshed. More investigation is needed to see if this is a good or bad thing
    // in the meantime once per user edit we will clear the current account Id so that the cap enabled state will be refreshed
    // consider a reset method? First must understand why this service hangs around while integration services do not before doing
    // any major work. Also where is this feature heading? Maybe a service with a event emitting? Tried that and this service did not
    // receive it. Something very specific about these product services that needs further looks once the feature has settled.
    this.currentAccountId = undefined;

    await this.initializeServices(user);
    // The MiVB product is a special snowflake. The account integration requirements changes with
    // whether there is a subscription on the account
    return this.ordersService.doesCustomerAccountHaveExistingProductSubsAndTier(
      environment.subscriptionIds.MiVoiceBusiness, ["MiVB_Premier",	"MiVB_Elite", "MiVB_Entry"]).then(existingSubs => {
        // actual licenses can only be assigned if CAP is OFF and there is a subscription
        // requires only the MiVoice Business integration and not a GW integration

        if ( this.capIsEnabled == false && existingSubs == true )
        {
          return accountIntegrations.includes('MiVB');
        }
        else if ( this.capIsEnabled == true)
        {
          if(existingSubs == true){
            // not supported but will check for all integrations
            // unpredictable results at this point
            console.warn("CAP being enabled and the account have existing license subscription is currently not supported. Unpredictabled behaviour may happen.");
          }

          //The code below should be executed when existingSubs == false. But for now, we will also do it when existingSubs == true
          //'MiVB' comes from having the MiVoice Business integration added to the account
          // mivbInNetworkElements will be true if a 3300 ICP exists in the network elements
          // if no 3300 ICP in network elements (could happen if not onboarded, not CLD setup, or call fails) - then return false
          // if here are tags suggesting a pbx was onboarded via the GWP, then confirm the pbx related tag is MiVoice Business

          if(accountIntegrations.includes('MiVB')){
            return this.mivbAccountSettingsService.isMiVBInNetworkElements().then((mivbInNetworkElements) => {
              let pbxsFoundInProductTags = [];

              //There could be pbx tags that are not MiVoice Business.
              //We'll only worry about them if the account has the CloudLink Gateway integration added
              if(accountIntegrations.includes('Gateway')){
                let tagObject = {
                  products: [...accountIntegrations]
                };
                pbxsFoundInProductTags = Util.getPbxTypesFromTags(tagObject);
                if(pbxsFoundInProductTags && pbxsFoundInProductTags.length > 0){
                  if(pbxsFoundInProductTags.length > 1 || pbxsFoundInProductTags[0] !== 'MiVoice Business'){
                    //product tags are not in correct state for mivb
                    //there are some tags that suggest at least one pbx that is not an mivb
                    //technically it would be great to also check how far along in onboarding they got in the gwp with this unexpected pbx
                    //but would need more account tags (on-bord-progress)
                    //perhaps knowing there exists tags from a different pbx type is enough
                    return false;
                  }
                }
              }

              if(mivbInNetworkElements){
                this.userMiVBEmailHistoryData.shouldEmailIconsShow = true;
                this.determineEmailIcon();
                return true;
              }

              return false;
            });
          }
          return false;
        }
          // does not matter
        else
        {
          return false;
        }
    });
  }

  async getConfigurationDialog(setupComplete?: boolean, user?: any): Promise<any> {

    let canAddLicenses: boolean = false;
    this.configurationDialogName = undefined;
    await this.initializeServices(user);

    if ( user )
      canAddLicenses = await this.userMivbSharedService.canAddMiVBLicensesToUsers( user );

    if ( canAddLicenses == true ){
      this.configurationDialogName = "MiVoiceBusinessConfigurationComponent";
      return MiVoiceBusinessConfigurationComponent;
    }

    if (!setupComplete) {
      this.configurationDialogName = "MivbLicenseConfigurationComponent";
      return MivbLicenseConfigurationComponent;
    }

    if (sessionStorage.getItem('section-settings')) {
      this.configurationDialogName = "MivbLicenseConfigurationComponent";
      return MivbLicenseConfigurationComponent;
    }

    return undefined;
  }

  async getInputDataForConfigurationDialog(user: User, data?: any): Promise<any> {
    let inputData = null;
    await this.initializeServices(user);

    switch (this.configurationDialogName) {
      case "MivbLicenseConfigurationComponent":
        //neither of the two calls below will fail/be rejected - they just return undefined
        //then it's up to the dialog to deal with undefined values the way it wants
        let networkElements = await this.mivbAccountSettingsService.getMiVBNetworkElements();
        inputData = {
          user: user,
          templateId: data?.templateId ? data.templateId : undefined, //sent to the dialog when we want to do a quickAdd
          networkElements: networkElements
        };
        break;

      default:
        break;
    }

    return Promise.resolve(inputData);
  }

  async initSecondaryComponent(user?: User) {
    return false;
  }

  async isSetupComplete(user: User): Promise<boolean> {
    await this.initializeServices(user);

    return this.userMivbSharedService.canAddMiVBLicensesToUsers( user ).then( async canAdd => {
      if ( canAdd == true )
      {
        return this.ordersService.getUserLicenses( user.userId ).then((userLicenses) => {
          const result = userLicenses.find( element => {
            return element.enable && mivoiceBusinessLicenses.includes(element.name);
          });
          return !!result;
        })
        .catch( (err) => {
          console.log('Failed to get user Licenses', err);
          return false;
        });
      }

      if (user.extension) {
        return true;
      }

      let cachedConfig = this.getCachedMivbConfig(user);
      if(this.isMiVBUserConfigurationComplete(cachedConfig)){
        return true;
      }

      return this.miVBUserSvc.getMiVBUserDetails(user).then(async details => {
        return details?.mivbConfiguration ? this.isMiVBUserConfigurationComplete(details?.mivbConfiguration) : false;
      });
    });
  }

   /**
   * Checks for MiVB configuration, in case the MiVB product was previously deleted
   * If the MiVB product was deleted -> user has pbxUserId defined, mivbConfiguration has no phones
   * @returns
   */
   isMiVBUserConfigurationComplete(mivbConfiguration: any): boolean {
    return !!(mivbConfiguration && mivbConfiguration.phones && mivbConfiguration.phones.length);
  }

  private async getMiVBUserServices(user: User): Promise<FormattedMiVBUserTelephonyService[]> {
    await this.initializeServices(user);
    if (this.capIsEnabled == false) {
      return Promise.resolve(undefined);
    }
    else // legacy licensing
    {
      return this.miVBUserSvc.getMiVBUserServices(user).then(async services => {
        return Promise.resolve(services ?? []);
      })
    }
  }

  async getHighLevelProductDetails(user: User): Promise<HighLevelProductDetail[]> {
    return this.getMiVBUserServices(user)
      .then(services => services?.map(s => ({ primary: s.primary, secondaries: s.secondaries })) ?? [])
  }

  async isSyncComplete(user: User): Promise<boolean> {
    await this.initializeServices(user);
    if ( this.capIsEnabled == false )
      return true; // there is no sync in this condition but there is no way to return that state

    return this.miVBUserSvc.getMiVBUserDetailsLiveSync(user).then(async details => {
      if (details?.mivbConfiguration) {
        this.cachedConfiguration = {
          userId: user.userId,
          mivbConfiguration: details.mivbConfiguration
        };
        await this.setTagsForExistingUser(user, details);
        return true;
      } else {
        return false;
      }
    });
  }

  //auto-add mivb products tag for users that were imported with an extension
  //only called when cap is enabled
  private async setTagsForExistingUser(user: User, details: any) {
    delete details.mivbConfiguration;
    const tags = details;
    let tagBody;
    if (user.extension && (!details.products || !details.products.includes('mivb'))) {
      tagBody = { products : []} as any;
      if (tags.products) {
        tagBody.products.push(...tags.products);
      }
      tagBody.products.push('mivb');

      await this.adminService.tryUpdateTagsOfUserByAccountId(user.accountId, user.userId, tagBody, tags);
    }
  };

  getWarningTooltip(): string {
    return 'users_edit_page.sync_mivb_user_failed';
  }

  templateHasProduct(template: any) {
    return template?.pbxConfig?.phones ? true : false;
  }

  getTemplateBadges(template: any) {
    let templateBadges = [];

    // learned badge
    if (template.pbxConfig?.userServiceTemplate) {
      templateBadges.push(TemplateBadges.LEARNED);
    }

    // mitel default badge
    if (template.pbxConfig?.uccServices?.defaultTemplate && template.pbxConfig?.uccServices?.defaultTemplate === true) {
      templateBadges.push(TemplateBadges.MITEL_DEFAULT);
    }

    if (template.parentTemplateId && template.pbxConfig?.userServiceTemplate) {
      templateBadges.push(TemplateBadges.CUSTOMIZED);
    }

    return templateBadges;
  }

  async handleProductStateChange(user: User, state: boolean, isSubComponent?: boolean): Promise<any> {
    await this.initializeServices(user);
    //user coming in may be out of date - get a fresh copy
    return this.adminService.getUserWithoutFrills(user.userId, 'tags').then( async freshUserWithTags => {
      let tags = freshUserWithTags.tags || {};
      console.log("UserWithTags for mivb state change: ", freshUserWithTags);

      let tagBody = {
        products: []
      };
      if (state) {
        tagBody.products.push('mivb');
        if (tags && tags.products) {
          tagBody.products.push(...tags.products);
        }

        let updatedTags = await this.adminService.tryUpdateTagsOfUserByAccountId(freshUserWithTags.accountId, freshUserWithTags.userId, tagBody, tags);
        console.log('updated tags after mivb state change', updatedTags);
      } else {
        this.cachedConfiguration = { userId: '', mivbConfiguration: {}}; //clear out cachedConfiguration if mivb product is removed from user
        if ( this.capIsEnabled == false ) {
          // real licenses are only assigned when CAP is OFF so it makes sense that they can only be removed then too
          return this.ordersService.getUserLicenses(user.userId).then( licenses => {
            const licenseToRemove = licenses.find(license => {
              return license.enable && mivoiceBusinessLicenses.includes(license.name);
            });

            if (tags && tags.products) {
              tagBody.products.push(...tags.products);
            }
            let ndx =  tagBody.products.indexOf('mivb');
            if(ndx > -1){
              tagBody.products.splice(ndx, 1);
            }
            if (licenseToRemove)
            {
              // Found the license to remove
              return this.ordersService.addOrRemoveUserLicenseWithoutErrorHandler(user.userId, licenseToRemove.name, false).then( result => {
                  this.ordersService.clearAcctSubscriptionsCache();
                  console.log(" user entitlement is removed successfully", result);
                  return this.adminService.tryUpdateTagsOfUserByAccountId(user.accountId, user.userId, tagBody, tags);
                })
            } else {
              // for the scenario where product is removed before setup is completed.
              return this.adminService.tryUpdateTagsOfUserByAccountId(user.accountId, user.userId, tagBody, tags);
            }
          });
        } else {
          this.ordersService.clearAcctSubscriptionsCache();
          await this.miVBUserSvc.deleteMiVBUser(freshUserWithTags, tags);
          await this.miccbService.deactivateMiCCBEmployee(user.accountId!, user.userId, user.email);
        }
      }
    });
  }

  isButtonDisabled(user: User, product: ProductLicense): Promise<boolean> {
    return Promise.resolve(false);
  }

  async isProductEnabled(user: User, isSubComponent?: boolean): Promise<boolean> {
    await this.initializeServices(user);

    if(user.extension && this.capIsEnabled){
      return Promise.resolve(true);
    }

    return this.adminService.getUserTagsByAccountIdWithoutSpinner(user.accountId, user.userId).then( tags => {
                              if (tags.products && tags.products.length > 0 && tags.products.indexOf('mivb') > -1) {
                                return true;
                              } else {
                                return false;
                              }
                            });

  }

  async getRemainingLicenses(user: User, isSubComponent?: boolean): Promise<number> {
    await this.initializeServices(user);
    if ( this.capIsEnabled == true )
    {
      return Promise.resolve(1000); // should this be removed? SHould only rely on real licenses?
    }
    return this.ordersService.getRemainingLicenses().then( remainingLicenses => {
      let totalLicenses = 0;
      for( let license in remainingLicenses) {
        if (mivoiceBusinessLicenses.includes(license)) {
          totalLicenses += remainingLicenses[license];
        }
      }
      return totalLicenses;
    }).catch(error => {
      console.log("Failed to get remaining mivoice business licenses", error);
      return 0;
    });
  }

  async getSubComponentConfigurationDetails ( user: User, product?: ProductLicense ): Promise<any>
  {
    await this.initializeServices(user);
    let details: any = {
      subComponentNames: ["meet", "chat"],
      subComponentLabel: "product_licenses.features",
      subComponent: [],
      subComponentEditable: false,
      secondaryServices: []
    }

    if ( this.capIsEnabled == true )
    {
      details.subComponentLabel = 'product_licenses.mivb.mivb_service_details';
      details.subComponentEditable = true;
      details.subComponentNames = undefined;
      if (!!(await this.zappaService.isLicenseEnabledForUser(user)))
      {
        details.secondaryServices.push(this.zappaService);
      }
      if (await this.miccbService.isMiCCBEnabled(user?.accountId))
      {
        details.secondaryServices.push(this.miccbService);
      }
    }

    return Promise.resolve( details );

  }

  getSubComponent(user: User, product: ProductLicense): Promise<any> {
    return Promise.resolve(product.subComponentNames);
  }

  async getConfigurationSubComponent(user: User) {
    await this.initializeServices(user);
    if ( this.capIsEnabled == true )
    {
      if (!sessionStorage.getItem('section-settings') || sessionStorage.getItem('section-configuration')) {
        return Promise.resolve(UserConfigurationComponent);
      }
    }

    return Promise.resolve(undefined);
  }

  async initConfigurationSubComponent(componentInstance: any, users: User[], isReadOnly?: boolean, asyncJobId?: string) {
    await this.initializeServices(users[0]);
    this.mivbAccountSettingsService.initializeMiVBAccountSubscription();
    this.subscription.add(
      componentInstance.initialized.subscribe(async () => {
        componentInstance.user = users[0];
        componentInstance.networkElements = await this.mivbAccountSettingsService.getMiVBNetworkElements();
        componentInstance.dynamicDimensions = await this.mivbAccountSettingsService.getAllDynamicDimensions();
        componentInstance.accountId = users[0].accountId;
        componentInstance.asyncJobId = asyncJobId ?? '';
        componentInstance.miccbEnabled = await this.miccbService.isMiCCBEnabled(users[0].accountId);
        componentInstance.managementProfile = this.rbacAuthGuardService.managementProfile;

        componentInstance.allInputsSentFromParent.next(true);
        console.log("All inputs sent to user config");
      })
    );

    this.subscription.add(
      componentInstance.processing.subscribe(async (event) => {
        if (event?.status === asyncJobProcessingStatus.START) {
          this.spinnerService.showSpinner();
          this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
        } else if (event?.status === asyncJobProcessingStatus.IN_PROGRESS){
          if(event.saveType && event.saveType === saveTypes.SAVE || event.saveType === saveTypes.SAVE_AND_DEPLOY){
            let asyncJobData = {
              asyncJobId: event?.asyncJobId,
              userId: users[0]?.userId,
              owner: asyncJobOwner.MIVB,
              dismissible: false,
              type: asyncJobStatus.IN_PROGRESS,
              postProcessingData: event.postProcessingData ? event.postProcessingData : null,
              link: `/accounts/${users[0].accountId}/users/${users[0].userId}/product-mivb?asyncJobId=${event?.asyncJobId}`
            } as MiVBAsyncJobData;
            asyncJobData.userName = await this.asyncJobsService.getUserName(asyncJobData.userId); //why no catch here?
            asyncJobData.message = this.translateService.instant('async_jobs.users_edit.in_progress_alert', { user: asyncJobData.userName ?? asyncJobData.userId });
            this.asyncJobsService.addAsyncJob(asyncJobData);
            this.mivbProvisioningService.removeUserFromCache(users[0]?.pbxUserId);
            this.ordersService.clearAcctSubscriptionsCache();
          }

          this.updateProdNLcnsEvent.next('inProgress');
          this.spinnerService.hideSpinner();

          if(event.saveType && event.saveType === saveTypes.SAVE_AND_DEPLOY || event.saveType === saveTypes.DEPLOY){
            let postProcessingData = event.postProcessingData? event.postProcessingData : null;
            try {
              if(event.saveType === saveTypes.SAVE_AND_DEPLOY && event?.asyncJobId?.length > 0){
                await this.asyncJobsService.pollAsyncJobStatusById(event.asyncJobId, 300000); //5min
              }

              let cloudLinkUser = postProcessingData?.user? postProcessingData.user : null;
              let languageCode = null;
              let translatedMiVBEmailStrings = null;
              let translatedMiCCEmailStrings = null;
              let miCollabIPFQDN = undefined;
              let passwords = postProcessingData?.passwords? postProcessingData.passwords : null;
              let withNotify = true; //we want to see notification banners for this deployment operation
              let getCloudLinkUserAndAccount = true; //don't have a fresh copy of the CloudLink user, nor the full account details - so get deployMiVBUser to look them up

              await this.mivbEmailService.deployMiVBUser(cloudLinkUser, languageCode, translatedMiVBEmailStrings, translatedMiCCEmailStrings, miCollabIPFQDN, passwords, withNotify, getCloudLinkUserAndAccount);

            } catch (error) {
              console.error("Error beginning deploy process for user");
              //should already be notified if save operation fails
              //if deployMiVBUSer fails - that method will throw up a notification banner
            }
          }
        }
      })
    );

    this.subscription.add(
      componentInstance.saving.subscribe(async (state) => {
        if (state) {
          this.spinnerService.showSpinner();
          this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
        } else { // saving is finished
          await this.postConfigurationSubComponentSave(users[0]);
          this.updateProdNLcnsEvent.next("resetList");
          this.spinnerService.hideSpinner();
        }
      })
    );

    this.subscription.add(
      componentInstance.formStatusUpdate.subscribe(status => {
        this.updateSubComponentFormStatus.next(status);
      })
    );

    this.subscription.add(
      componentInstance.error?.subscribe(error => {
        console.log("Error from the user config component (from MiVB component)", error);
        this.spinnerService.hideSpinner();

        this.updateProdNLcnsEvent.next({ error: error });
      })
    );

    this.subscription.add(
      componentInstance.elementIdForHeightExtension?.subscribe(async (elementId: string) => {
        let heightToExtend: number = await this.calculateHeightToExtendParentContainer(elementId);
          this.heightToExtendParentContainer.next(heightToExtend);
      })
    );
    return Promise.resolve();
  }

  private async calculateHeightToExtendParentContainer(elementId: string): Promise<number> {
    let heightToExtend = 0;
    if(!elementId){
      return Promise.resolve(0);
    }
    return new Promise(resolve => {
      setTimeout(() => {
        let childElement = document.getElementById(elementId);
        if(childElement){
          let childElementBottom = childElement.getBoundingClientRect().bottom + 10 ; //10 is just a bit more for aesthetics
          let containerElement = document.getElementById('app-products-licenses-item-config-container');
          let containerElementBottom = containerElement.getBoundingClientRect().bottom;

          /*
          - heightToExtend will be a positive number when we need to extend the container to fit the child
          - heightToExtend will be a negative number when we could shrink the container a bit (sometimes the child
            changes in height e.g if it's showing a warning or not)
          */
          heightToExtend = childElementBottom - containerElementBottom;
        }
        resolve(heightToExtend);
      }, 50); //a little time to be sure elementId is in DOM
    });
  }

  private async addOrRemoveLicenses(user: User) {
    await this.initializeServices(user);
    return this.miVBUserSvc.getMiVBUserDetails(user).then(async details => {
      //leaving this code for now as it will require a revisiting later for the mivb subscription when CAP is on
      if (details?.license) {
        if (details?.license && ['mivb_entry', 'mivb_premier', 'mivb_elite'].indexOf(details?.license.tier) > -1) {
          let licenseName;

          if (details.license.tier === 'mivb_entry') {
            licenseName = "MitelOne_Basic";
          } else if (details?.license.tier === 'mivb_premier') {
            licenseName = "MitelOne_Premier";
          } else {
            licenseName = "MitelOne_Elite";
          }

          let body: any = [{
            name: licenseName,
            enable: true,
            count: 1
          }];
          let licensesToRemove = mitelOneLicenses.filter(license => license != licenseName);

          for (let j = 0; j < licensesToRemove.length; j++) {
            body.push({
              name: licensesToRemove[j],
              enable: false,
              count: 1
            })
          }

          await this.ordersService.addOrRemoveMultipleUserLicense(user.userId, body);
        }
      }
    }).catch(error => {
      console.error("An error occurred while performing post Mivb save operation", error);
    });
  }

  async postConfigurationComponentSave(user: User) {
    sessionStorage.setItem('section-configuration', 'mivb');
    return Promise.resolve(true);
  }

  async postConfigurationSubComponentSave(user: User) {
    await this.initializeServices(user);
    await this.addOrRemoveLicenses(user);
    return this.miVBUserSvc.getMiVBUserDetails(user).then(async details => {
      if (details?.mivbConfiguration) {
        this.cachedConfiguration = {
          userId: user.userId,
          mivbConfiguration: details.mivbConfiguration
        };
      }
    });
  }

  getUpdateProdNLcnsSecEventFromConfigComp() {
    return this.updateProdNLcnsEvent;
  }

  getUpdateSubComponentFormStatus() {
    return this.updateSubComponentFormStatus;
  }

  async getConfigurationSubComponentDisplayName(user: User, componentInstance: any): Promise<string> {
    await this.initializeServices(user);
    if ( this.capIsEnabled == false )
      {
        return Promise.resolve(undefined);
      }
      return Promise.resolve('product_config.UserConfigurationComponent');
  }

  async getProductLicenseSection(user: User, setupComplete?: boolean): Promise<any> {
    await this.initializeServices(user);

    if ( this.capIsEnabled == false )
    {
      return await this.getDisplayNameForLicense( user );
    }
    else // CAP is on, use the existing services view
    {
      let mivbServices = [];
      if (setupComplete) {
        mivbServices = await this.getMiVBUserServices(user);
      }
      let section = this.getSectionBasedOnService(mivbServices);
      return Promise.resolve(section);
    }
  }

  async getDisplayNameForLicense(user: User): Promise<any>
  {
    await this.initializeServices(user);
    let element = {
      displayName: '',
      bundleName: '',
      bundleCanBeEdited: false
    };
    element["bundleName"] = ProductLicenseBundleName.OTHER;

    // real licenses are only assigned when CAP is OFF
    return this.ordersService.getUserLicenses(user.userId).then( licenses => {
      const licenseAdded = licenses.find(license => {
        return license.enable && mivoiceBusinessLicenses.includes(license.name);
      });

      if(licenseAdded) {
        element["displayName"] =  "product_licenses.mivb." + licenseAdded.name;
      } else {
        element["displayName"] =  "product_licenses.mivb.no_license_assigned";
      }
      return element;
    }).catch(err => {
      console.log("Failed to get user licenses to display appropriate name for MiVoice Business");
      element["displayName"] =  "product_licenses.mivb.name";
      return element;
    });
  }

  getSectionBasedOnService(mivbServices: any) {
    let element = {
      displayName: '',
      bundleName: '',
      bundleCanBeEdited: false
    };
    if (mivbServices && mivbServices.length) {
      const saUCCUserLicense = 'UCC User License'
      element['sectionName'] = 'product_licenses.mivb.name';
      element['bundleName'] = ProductLicenseBundleName.MIVB;
      element['bundleCanBeEdited'] = true;
      let uccUserLicense = mivbServices.filter(service => 'serviceAlias' in service && service['serviceAlias'].toLocaleLowerCase() === saUCCUserLicense.toLocaleLowerCase());
      if (uccUserLicense && uccUserLicense.length) {
        element['displayName'] = 'product_licenses.mivb.ucc_user';
        if (uccUserLicense[0].serviceDetails && uccUserLicense[0].serviceDetails.uccBundleName) {
          let license = this.getLicenseByNameProperty(uccUserLicense[0].serviceDetails.uccBundleName);
          if (license) {
            element['displayName'] = `product_licenses.mivb.${license.type}.${license.tier}`;
          }
        }
      } else {
        element['displayName'] = 'product_licenses.mivb.build_your_own';
      }
    } else {
      element['bundleName'] = ProductLicenseBundleName.OTHER;
      element['displayName'] = 'product_licenses.mivb.no_service_assigned';
    }

    return element;
  }

  getLicenseByNameProperty(uccBundleName: string): any {
    var license = undefined;

    let bundleName = uccBundleName.toLocaleLowerCase();
    try {
      let uccV4 = [licenseUCCV40Tier.BASIC, licenseUCCV40Tier.STANDARD, licenseUCCV40Tier.PREMIUM, licenseUCCV40Tier.HOTDESK_SIP_SOFTPHONE];
      for (var i = 0; i < uccV4.length; i++) {
        if (bundleName.replace(/ /g, '_').indexOf(uccV4[i]) !== -1) {
          license = {
            type: miVBLicenseType.UCC_V4_0,
            tier: uccV4[i]
          };
        }
      }
      let uccEd2 = [licenseUCCEdition2Tier.TELEPHONY, licenseUCCEdition2Tier.PREMIER, licenseUCCEdition2Tier.ELITE_CX];
      for (var i = 0; i < uccEd2.length; i++) {
        if (bundleName.replace(/ /g, '_').indexOf(uccEd2[i]) !== -1) {
          license = {
            type: miVBLicenseType.UCC_EDITION_2,
            tier: uccEd2[i]
          };
        }
      }
      if (!license && bundleName.indexOf(licenseUCCV40Tier.ENTRY) !== -1) {
        if (bundleName.indexOf('(v4.0)') !== -1) {
          license = {
            type: miVBLicenseType.UCC_V4_0,
            tier: licenseUCCV40Tier.ENTRY
          };
        } else if (bundleName.indexOf('(ed. 2)') !== -1) {
          license = {
            type: miVBLicenseType.UCC_EDITION_2,
            tier: licenseUCCEdition2Tier.ENTRY
          };
        }
      }
    } catch (error) {
      console.log(`Error while setting up the license for uccBundleName: ${uccBundleName} -> error ${error}`);
    }
    return license;
  }

  async initialConfigurationSubComponentCall(componentInstance: any, user: User) {
    await Util.timeout(10);
    return componentInstance["saveConfiguration"]();
  }

  private getCachedMivbConfig(user: User) {
    if(this.cachedConfiguration && this.cachedConfiguration.userId === user.userId && user.pbxUserId) {
      return this.cachedConfiguration.mivbConfiguration;
    }
  }

  getSubComponentToC(componentInstance: any) {
    let contentList = [] as any;
    if (componentInstance.getTableOfContents) {
      contentList = componentInstance.getTableOfContents();
    }
    if (componentInstance.tableOfContentsItemChanged) {
      this.subscription.add(
        componentInstance.tableOfContentsItemChanged.subscribe(updatedTableOfContentsList => {
          this.cachedTableOfContentsList = updatedTableOfContentsList;
          this.updateSubComponentToC.next(this.cachedTableOfContentsList);
        })
      );
    }
    this.cachedTableOfContentsList = contentList;
    return contentList;
  }

  async expandSubComponentToC(componentInstance: any, contentId: string) {
    if (componentInstance.expandForTableOfContents)
      await componentInstance.expandForTableOfContents(contentId);
  }

  getUpdateSubComponentToC() {
    return this.updateSubComponentToC;
  }

  async showTableOfContents(user: User): Promise<boolean>
  {
    await this.initializeServices(user);
    return this.capIsEnabled;
  }

  isWideConfig() {
    return true;
  }

  extendHeightofParentContainer() {
    return this.heightToExtendParentContainer;
  }

  private resetServiceVariablesIfRequired(user: User){
    if(this.currentUserId){
      if(!user || !user.userId ||(this.currentUserId !== user.userId)){
        this.resetServiceVariables();
      }
    }
  }

  private resetServiceVariables(){
    this.currentUserId = undefined;
    this.userMiVBEmailHistoryData = {
      userId: undefined,
      accountId: undefined,
      shouldEmailIconsShow: false,
      deploymentEmailHistory: undefined
    };
    if(this.subscription){
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
    if(this.userTagsChangedSubscription){
      this.userTagsChangedSubscription.unsubscribe();
      this.userTagsChangedSubscription = undefined;
    }
    this.clearEmailTimeout();
  }

  /*
  Only call with tags if you're sure they are 100% up to date (no email operation in progress).
  Otherwise, we assume calling out for fresh tags is required.
  (I have doubts about if the user details are always current when the user object is passed to this service)
  */
  private async setUserMiVBEmailHistoryData(user: User, tags?: any): Promise<any>{
    try {
      this.userMiVBEmailHistoryData.userId = user.userId;
      this.userMiVBEmailHistoryData.accountId = user.accountId;

      if(!tags){
        tags = await this.adminService.getUserTagsByAccountIdWithoutSpinner(user.accountId, user.userId);
      }
      //if there are deploymentEmailHistory and they have the correct structure, store them
      if (tags && 'mivb' in tags &&
          "deploymentEmailHistory" in tags.mivb &&
          "inProgressOn" in tags.mivb.deploymentEmailHistory &&
          "succeededOn" in tags.mivb.deploymentEmailHistory &&
          "failedOn" in tags.mivb.deploymentEmailHistory &&
          "latest" in tags.mivb.deploymentEmailHistory
      ){
        this.userMiVBEmailHistoryData.deploymentEmailHistory = _.cloneDeep(tags.mivb.deploymentEmailHistory) as UserMiVBDeploymentEmailHistoryTagsBody;
      }
      else{
        throw 'missing deploymentEmailHistory tags or incorrect structure'
      }
    } catch (error) {
      console.error('Error determining mivb email icon: ', error);
      this.userMiVBEmailHistoryData.deploymentEmailHistory = undefined;
    }
  }

  private isInProgressEmailStateValid(timeStamp: string){

    if(timeStamp){
      let inProgressISODate = new Date(timeStamp).valueOf();
      let currentDate = new Date().valueOf();
      if(currentDate && inProgressISODate){
        let elapsedMinutes:number = (currentDate - inProgressISODate)/60000; //convert milliseconds to minutes
        if(elapsedMinutes && elapsedMinutes > 0 && elapsedMinutes < this.emailInProgressMaxMinutes){
          return true;
        }
      }
    }
    return false;
  }

  private clearEmailTimeout(){
    if(this.emailInProgressTimeoutId){
      clearTimeout(this.emailInProgressTimeoutId);
      this.emailInProgressTimeoutId = undefined;
    }
  }

  private startEmailInterval(user: User){
    this.clearEmailTimeout();

    let delay = this.emailInProgressMaxMinutes * 60000; //convert to milliseconds
    this.emailInProgressTimeoutId = setTimeout(async(user: User) => {
      try {
        await this.setUserMiVBEmailHistoryData(user); //call without tags to get a fresh copy and see if email history tags have changed
        this.determineEmailIcon();
      } catch (error) {
        this.determineEmailIcon();
      }
    }, delay, user);
  }

  private determineEmailIcon(){
    let icon = null;
    this.clearEmailTimeout();

    if(this.userMiVBEmailHistoryData.userId && this.userMiVBEmailHistoryData.accountId &&
      this.userMiVBEmailHistoryData.shouldEmailIconsShow && this.userMiVBEmailHistoryData.deploymentEmailHistory){

      if (this.userMiVBEmailHistoryData.deploymentEmailHistory.latest === UserMiVBEmailEvents.INPROGRESS){
        //is the inprogress state too old? Maybe the operation was interrupted and never completed nor failed
        //warn the user instead of constantly showing a spinner erroneously giving them hope that something productive is happening
        let isStateValid = this.isInProgressEmailStateValid(this.userMiVBEmailHistoryData.deploymentEmailHistory.inProgressOn);
        if(isStateValid){
          icon = _.cloneDeep(this.emailIcons[0]); //spinner
          let timeStamp = new Date(this.userMiVBEmailHistoryData.deploymentEmailHistory.inProgressOn).toLocaleString();
          icon.tooltipData.timestamp = timeStamp;
          //start interval to wait a max reasonable time for email operation to complete
          //if the timer
          this.startEmailInterval({userId: this.userMiVBEmailHistoryData.userId, accountId: this.userMiVBEmailHistoryData.accountId});
        }
        else{
          icon = _.cloneDeep(this.emailIcons[1]); //caution
          let timeStamp = new Date(this.userMiVBEmailHistoryData.deploymentEmailHistory.inProgressOn).toLocaleString();
          icon.tooltipData.timestamp = timeStamp;
        }
      }
      else if(this.userMiVBEmailHistoryData.deploymentEmailHistory.latest === UserMiVBEmailEvents.SUCCEEDED){
        icon = _.cloneDeep(this.emailIcons[2]);
        let timeStamp = new Date( this.userMiVBEmailHistoryData.deploymentEmailHistory.succeededOn).toLocaleString();
        icon.tooltipData.timestamp = timeStamp;
      }
      else if(this.userMiVBEmailHistoryData.deploymentEmailHistory.latest === UserMiVBEmailEvents.FAILED){
        icon = _.cloneDeep(this.emailIcons[3]);
        let timeStamp = new Date(this.userMiVBEmailHistoryData.deploymentEmailHistory.failedOn).toLocaleString();
        icon.tooltipData.timestamp = timeStamp;
      }
    }

    this.updateEmailIcon.next(icon);
  }

  /*
  - Initializes the data needed for determining the email icon.
  - Then calls determineEmailIcon to figure out which is the correct icon to show and triggers updateEmailIcon
  - If something fails, we just won't show an icon
  - If userMiVBEmailHistoryData already has a userId, then setUserMiVBEmailHistoryData has already been initialized
  */
  async evaluateEmailIcon(user: User): Promise<void> {
    try {
      // initializeServices will reset the email tags, and make sure the userTagsChangedSubscription is set
      // The subscription was likely set earlier when initializeServices was called by some other action for this user
      await this.initializeServices(user);

      if(user.userId && user.accountId){
        if(!this.userMiVBEmailHistoryData.userId){
          //Have never populated the variable yet for this user.
          //Have never called out for fresh tags for this purpose before
          await this.setUserMiVBEmailHistoryData(user);
        }
        this.determineEmailIcon();
        return Promise.resolve();
      }
      throw 'missing basic user details';
    } catch (error) {
      console.error("Error getting email icon for user's mivb product");
      return Promise.resolve(); //resolve don't reject. No icon will show, that's ok
    }
  }

  emailIconChanged() {
    return this.updateEmailIcon;
  }

  destroySubscriptions() {
    this.resetServiceVariables();
  }
}
